const debounce = require('lodash/debounce');
const { isFunction, isObject } = require('./type');
const { identity, complement } = require('./fp');
const createCache = require('./cache');

const isNotObject = complement(isObject);
/**
 * Create a new in-memory storage
 * @param {Object} populateWithItems key/item pairs for having initial values
 * @returns {Storage} New memory storage
 * @throws {TypeError}
 */
function createInMemoryStorage(populateWithItems = {}) {
    if (arguments.length > 0 && isNotObject(populateWithItems)) {
        throw new TypeError(
            'It can be populated with a key/value object only.'
        );
    }
    /** @type {Storage} Custom-made memory storage */
    const memoryStorage = {
        items: populateWithItems,
        get length() {
            return Object.keys(this.items).length;
        },
        getItem(key) {
            return this.items[key] ?? null;
        },
        key(n) {
            return Object.keys(this.items).at(n);
        },
        removeItem(key) {
            delete this.items[key];
            this.items[key] = undefined;
        },
        setItem(key, item) {
            this.items[key] = item;
        },
        clear() {
            this.items = {};
        },
        raw() {
            return this.items;
        }
    };
    return memoryStorage;
}

/**
 * @typedef {object} StorageLens An object who provides a getter and a setter of
 * a whole storage based on another. Example: the data structure (key/value)
 * that is in the entry 'searchData' in LocalStorage (storage-in-storage model)
 * @property {function(Storage):Storage} get Get the storage from a given
 * high-order storage
 * @property {function(Storage, Storage):Storage} set Set the storage into the
 * high-order storage
 */

/**
 * Create a Lens of an Storage
 * @param {function(Storage):Storage} getter getter function
 * @param {function(Storage, Storage):Storage} setter setter function
 * @returns {StorageLens} A lens for Storage
 */
function createStorageLens(
    getter, // (backStorage) => storage
    setter // (storage, backStorage) => storage
) {
    return {
        get: getter,
        set: setter
    };
}

/**
 * Create a Lens of an Storage that is backened by an exclusive Storage. So, the
 * target Storage is the same that the back storage.
 * @returns {StorageLens}  The needed StorageLens for the given purpose
 */
function createStorageLensForDedicatedStorage() {
    return createStorageLens(
        identity, // Dumb getter, Identity
        (val, backStorage) => {
            if (val === null) {
                backStorage.clear();
            }
            // Otherwise a dumb setter
            return backStorage;
        }
    );
}

/**
 * Create a Lens of an Storage that is serialized in a single entry of another
 * Storage
 * @param {string} backStorageKey The key of the entry
 * @returns {StorageLens} The needed StorageLens for the given purpose
 */
function createStorageLensForSingleEntryOfStorage(backStorageKey) {
    const cachedStorage = createCache(3000);

    /**
     * Get the data of high-level storage from the real storage
     *
     * @param {Storage} backStorage the real storage where the storage is get
     *  from
     * @returns {Storage} high-level storage
     */
    function getter(backStorage) {
        let storage = cachedStorage.get();
        if (!storage) {
            const stringifiedStorage = backStorage.getItem(backStorageKey);
            if (stringifiedStorage === null) {
                storage = createInMemoryStorage();
            } else {
                try {
                    storage = createInMemoryStorage(
                        JSON.parse(stringifiedStorage)
                    );
                } catch (e) {
                    // eslint-disable-next-line no-console
                    console.log(
                        `The key ${backStorageKey} contains data with invalid format and it must be reset`,
                        stringifiedStorage
                    );
                    storage = createInMemoryStorage();
                }
            }
            cachedStorage.set(storage);
        }
        return storage;
    }

    /**
     * Set into backStorage using debounce for better performance. Then, when
     * there is concurrency the writting will be at the end, saving a lot of
     * unnecessary writtings in an entry of localStorage or other Storage.
     *
     * @param {Storage} storage
     * @param {Storage} backStorage
     * @returns {Storage} setted storage
     */
    const debouncedSetter = debounce((storage, backStorage) => {
        if (storage === null) {
            backStorage.removeItem(backStorageKey);
        } else {
            backStorage.setItem(backStorageKey, JSON.stringify(storage.raw()));
        }
        cachedStorage.clear();
    }, 300);

    /**
     * Set the data of high-level storage into the real storage
     *
     * @param {Storage} storage the high-level storage
     * @param {Storage} backStorage the real storage where the storage is saved
     * @returns {Storage} setted storage
     */
    function setter(storage, backStorage) {
        const isStorageSupported = storage === null || isFunction(storage?.raw);
        if (!isStorageSupported) {
            throw new TypeError('Given storage is not supported by the setter');
        }
        // Update the cached storage for instant response
        cachedStorage.set(storage);
        // Update the real storage
        debouncedSetter(storage, backStorage);
        return storage;
    }

    return createStorageLens(getter, setter);
}

/**
 * Create new logical Storage of key/item pairs backened by a real Storage
 * (memory, localStorage, sessionStorage...)
 *
 * @param {Object} options The hashmap config
 * @param {Storage} options.backStorage The base storage which stores the data
 * of this abstraction
 * @param {StorageLens} options.storageLens The lens to get/set our logical
 * Storage from the back storage
 * @param {function(any)} [options.validateItem] Function that throws a
 * TypeError if the item does not have a desired format.
 * @param {function(string)} [options.validateKey] Function that throws a
 * TypeError if the key does not have a desired format.
 * @returns {Storage} new Storage of key/item pairs
 */
function createHashmapStorage({
    backStorage,
    storageLens,
    validateItem = identity,
    validateKey = identity
}) {
    const instance = {
        get length() {
            return storageLens.get(backStorage).length;
        },
        /**
         * When invoked, will empty all keys out of the storage.
         */
        clear() {
            storageLens.set(null, backStorage);
        },
        /**
         * When passed a number n, this method will return the name of the nth key
         * in the storage.
         * @param {number} n ordinal
         * @returns {string} the name of the nth key
         */
        key(n) {
            return storageLens.get(backStorage).key(n);
        },
        /**
         * When passed a key name, will return that key's value.
         * @param {string} key key name
         * @returns {any} value stored for the given key
         * @throws {TypeError}
         */
        getItem(key) {
            validateKey(key);
            return storageLens.get(backStorage).getItem(key);
        },
        /**
         * When passed a key name, will remove that key from the storage.
         * @param {string} key key name
         * @throws {TypeError}
         */
        removeItem(key) {
            validateKey(key);
            const storage = storageLens.get(backStorage);
            storage.removeItem(key);
            storageLens.set(storage, backStorage);
        },
        /**
         * When passed a key name and value, will add that key to the storage, or
         * update that key's value if it already exists.
         * @param {string} key key name
         * @param {any} item value
         * @returns {any} the setted value
         * @throws {TypeError}
         */
        setItem(key, item) {
            validateKey(key);
            validateItem(item);
            const storage = storageLens.get(backStorage);
            storage.setItem(key, item);
            storageLens.set(storage, backStorage);
            return item;
        }
    };

    return instance;
}

/**
 * The responsible of assuring that there cannot exists two Storages saved in
 * the same key of an key/value based Storage (ex: LocalStorage, SessionStorage)
 */
const keymaster = {
    registry: new Set(),
    /**
     * Add a key for an Storage
     * @param {string} key The tested key
     * @param {HASHMAP_STORAGE_TYPE} type The targeted Storage
     */
    addKeyForStorageType(key, type) {
        if (this.isKeyUsedForStorageType(key, type)) {
            throw new TypeError(
                `Key ('${key}') is already used for Storage of type ${type}`
            );
        }
        this.registry.add(this.hash(key, type));
    },
    /**
     * Check a hash of a type/key pair
     * @param {string} key The tested key
     * @param {HASHMAP_STORAGE_TYPE} type The targeted Storage
     * @returns {string} hash
     */
    hash(key, type) {
        return `T${type}_K${key}`;
    },
    /**
     * Check if a key is already in use.
     * @param {string} key The tested key
     * @param {HASHMAP_STORAGE_TYPE} type The targeted Storage
     * @returns {boolean} true if it is already used
     */
    isKeyUsedForStorageType(key, type) {
        return this.registry.has(this.hash(key, type));
    }
};

/**
 * Enum for defined
 * @readonly
 * @enum {number}
 */
const HASHMAP_STORAGE_TYPE = {
    IN_MEMORY_STORAGE: 1,
    LOCAL_STORAGE: 2,
    SESSION_STORAGE: 3
};

const HashmapStorageFactory = {
    /**
     * Create an Storage to store key/item pairs
     * @param {Object} options Creation data
     * @param {HASHMAP_STORAGE_TYPE} options.type the type of storage
     * @param {string} [options.name] the name of the storage in snake-case
     * @param {function(any)} [options.validateItem] Function that throws a
     * TypeError if the item does not have a desired format.
     * @param {function(string)} [options.validateKey] Function that throws a
     * TypeError if the key does not have a desired format.
     * @returns {Storage} new instance
     * @throws {TypeError}
     */
    createHashmapStorage({
        type,
        name,
        validateItem = identity,
        validateKey = identity
    }) {
        let instance;
        if (type === HASHMAP_STORAGE_TYPE.IN_MEMORY_STORAGE) {
            instance = createHashmapStorage({
                backStorage: createInMemoryStorage(),
                storageLens: createStorageLensForDedicatedStorage(),
                validateItem,
                validateKey
            });
        } else if (
            type === HASHMAP_STORAGE_TYPE.LOCAL_STORAGE
            || type === HASHMAP_STORAGE_TYPE.SESSION_STORAGE
        ) {
            if (!name) {
                throw new TypeError(
                    '"name" argument needed for this type of storage'
                );
            }
            const backStorage = type === HASHMAP_STORAGE_TYPE.LOCAL_STORAGE
                ? localStorage
                : sessionStorage;
            instance = createHashmapStorage({
                backStorage,
                storageLens: createStorageLensForSingleEntryOfStorage(name),
                validateItem,
                validateKey
            });
            keymaster.addKeyForStorageType(name, type);
        } else {
            throw new TypeError('Unknown storage type');
        }
        return instance;
    }
};

module.exports = {
    HashmapStorageFactory,
    HASHMAP_STORAGE_TYPE
};
